// /en/police-clearance-foreigner/$nationality/embassy-legalization — EN
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { FOREIGNER_NATIONALITIES, getForeignerNationality } from "@/data/police-foreigner-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { Stamp, MessageCircle, Phone, BadgeCheck, ListChecks, Globe2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/police-clearance-foreigner/$nationality/embassy-legalization")({
  loader: ({ params }) => {
    if (!getForeignerNationality(params.nationality)) throw notFound();
    return { slug: params.nationality };
  },
  head: ({ params }) => {
    const n = getForeignerNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("Not found", "/en/police-clearance-foreigner/$nationality/embassy-legalization", "en")}, { name: "robots", content: "noindex" }] };
    const url = `${SITE}/en/police-clearance-foreigner/${n.slug}/embassy-legalization`;
    const chain = n.hague ? "Apostille + Thai MFA" : `${n.embassyBkkEn} + Thai MFA`;
    const title = `Legalize ${n.homeAuthority} via ${chain} for ${n.nameEn} in Thailand | NYC`;
    const desc = `Full legalization chain for ${n.countryEn} Police Clearance (${chain}) — ${n.timelineDays}. Free consultation, fast reply.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/police-clearance-foreigner/$nationality/embassy-legalization", "en") }, { name: "description", content: ctaDesc(desc, "/en/police-clearance-foreigner/$nationality/embassy-legalization", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/police-clearance-foreigner/$nationality/embassy-legalization", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/police-clearance-foreigner/$nationality/embassy-legalization", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(
          `/police-clearance-foreigner/${n.slug}/embassy-legalization`,
          `/en/police-clearance-foreigner/${n.slug}/embassy-legalization`,
        ),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `Legalize ${n.homeAuthority} + ${chain}`,
              serviceType: "Police Clearance Legalization",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: n.countryEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: n.priceFromTh + 2500, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
        ]),
      ],
    };
  },
  component: Page,
});

function Page() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getForeignerNationality(slug)!;
  const related = FOREIGNER_NATIONALITIES.filter((x) => x.slug !== slug).slice(0, 8);
  const price = n.priceFromTh + 2500;
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/en/police-clearance-foreigner" className="hover:text-gold">Foreigners in Thailand</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/en/police-clearance-foreigner/$nationality" params={{ nationality: slug }} className="hover:text-gold">{n.nameEn}</Link><span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">Legalization</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Stamp className="h-3.5 w-3.5" /> {n.hague ? "Apostille" : "Embassy"} + Thai MFA
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Legalize {n.homeAuthority} for {n.nameEn} Nationals in Thailand
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            {n.hague
              ? `Apostille at ${n.countryEn} competent authority → certified Thai translation → MFA endorsement. Accepted by Thai Immigration, Amphur, BOI, and PR office.`
              : `Notary → ${n.countryEn} Foreign Ministry → ${n.embassyBkkEn} → certified Thai translation → Thai MFA. Complete chain for Thai official use.`}
            {" · "}{n.timelineDays} · from THB {price.toLocaleString()}
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          {n.hague
            ? `${n.countryEn} is a Hague Apostille member — ${n.homeAuthority} can be Apostilled at the country of origin and used in Thailand after certified Thai translation and MFA endorsement.`
            : `${n.countryEn} is NOT a Hague member — Police Clearance requires full legalization chain: Notary → ${n.countryEn} Foreign Ministry → ${n.embassyBkkEn} → certified Thai translation → Thai MFA endorsement.`}
        </blockquote>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <ListChecks className="h-6 w-6 text-amber-600" /> Legalization Chain
          </h2>
          <ol className="mt-5 space-y-3 text-[15px]">
            {(n.hague
              ? [
                  `Obtain ${n.homeAuthority} from ${n.countryEn}`,
                  `Apostille at ${n.countryEn} Competent Authority`,
                  `Courier back to Thailand (tracked)`,
                  `Certified Thai translation (MFA-registered translator)`,
                  `Endorsement at Thai MFA Consular Affairs (Chaengwattana)`,
                  `Delivery of Apostilled originals + MFA-certified Thai translation`,
                ]
              : [
                  `Obtain ${n.homeAuthority}`,
                  `Notarize at ${n.countryEn} Notary Public`,
                  `Legalize at ${n.countryEn} Foreign Ministry`,
                  `Legalize at ${n.embassyBkkEn}`,
                  `Certified Thai translation + Thai MFA endorsement`,
                  `Delivery: ready to file at Immigration / Amphur / PR`,
                ]
            ).map((s, i) => (
              <li key={i} className="rounded-2xl border border-slate-200 bg-white p-4">
                <strong className="text-navy-deep">{i + 1}.</strong> {s}
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">FAQ</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `Does my ${n.countryEn} PC need Apostille or Embassy Legalization?`, a: n.hague ? `Apostille at ${n.countryEn} (Hague member since the 1961 Convention).` : `Full-chain legalization: Notary → Foreign Ministry ${n.countryEn} → ${n.embassyBkkEn}.` },
                { q: "Do I still need Thai translation + MFA?", a: "Yes — regardless of whether the source certificate carries Apostille or full Consular Legalization from the destination country, Thai government authorities (Immigration, Ministry of Labour, Amphoe registrar) require certified Thai translation by a registered translator plus Thai MFA Department of Consular Affairs endorsement before the document is legally usable inside Thailand." },
                { q: "Total processing time?", a: `${n.timelineDays} end-to-end.` },
                { q: "All-inclusive price?", a: `From THB ${price.toLocaleString()} — includes PC + ${n.hague ? "Apostille" : "Legalization"} + Thai translation + MFA.` },
                { q: "Document validity?", a: "Thai Immigration / Amphur typically requires PC dated within 3 months — all legalization steps must complete within that window." },
              ]}
            />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Globe2 className="h-6 w-6 text-amber-600" /> Legalization for Other Nationalities
          </h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/en/police-clearance-foreigner/$nationality/embassy-legalization" params={{ nationality: r.slug }}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:border-gold/50">
                <span className="font-semibold">{r.homeAuthority}</span>
                <span className="ml-auto text-[10px] text-slate-500">{r.hague ? "Apostille" : "Legalize"}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
      <QuotableStatsStrip cluster="police" locale="en" variant="light" />
    </main>
  );
}
